import { useFormContext } from "react-hook-form";
import {
  AlignItems,
  CSSRulesFunction,
  Field,
  Input,
  Label,
  Text,
  Tooltip,
  XYGrid,
  useTheme,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { SignupFormData } from "../../consts";

import { CountryDropdown } from "./CountryDropdown";

import { useSegment } from "src/technical-integrations/segment/useSegment";
import { TrackingEvent } from "src/common/trackingEvents";
import { getInputErrorStatus } from "src/utils/inputValidation";

const businessNameStyle: CSSRulesFunction = (theme) => ({
  div: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    "&:focus-within": {
      zIndex: 1,
      position: "relative",
    },
  },
  "div[role='tooltip']": {
    borderRadius: theme.radius(1),
  },
});

interface Props {
  userCountryCode: string;
}

export const BusinessNameField: React.FC<Props> = ({ userCountryCode }) => {
  const { sendEvent } = useSegment();
  const { theme } = useTheme();
  const {
    register,
    formState: { errors },
  } = useFormContext<SignupFormData>();
  const { i18n } = useLingui();

  const businessNameLabel = i18n._({
    id: "optimised-sign-up.form.business-name",
    message: "Business name and location",
  });

  return (
    <Field id="business_name_field" css={businessNameStyle(theme)}>
      <Label htmlFor="business_name" aria-label={businessNameLabel}>
        <Trans id="optimised-sign-up.form.business-name">
          Business name and location
        </Trans>
        <Text>
          <Tooltip
            message={
              <Trans id="optimised-sign-up.form.business-name-tooltip">
                Enter registered business name and country. For individuals this
                can be your full name or trading name and the country you do
                business in.
              </Trans>
            }
            triggeredBy="click"
          >
            <Trans id="optimised-sign-up.form.business-name">
              Business name and location
            </Trans>
          </Tooltip>
        </Text>
      </Label>
      <XYGrid templateColumns="1fr 95px" alignItems={AlignItems.End}>
        <Input
          type="text"
          id="business_name"
          status={getInputErrorStatus(!!errors.business_name)}
          autoComplete="on"
          {...register("business_name", {
            required: i18n._({
              id: "optimised-sign-up.form.business-name.required",
              message: "Business name is required",
            }),
            onBlur: () => {
              sendEvent(TrackingEvent.SIGNUP_FORM_FIELD_INTERACTION, {
                label: "business_name",
                type: "Blur",
              });
            },
          })}
        />
        <CountryDropdown userCountryCode={userCountryCode} />
      </XYGrid>
    </Field>
  );
};
